import { useEffect, useMemo, useRef } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import useOpenInterest from '@/features/markets/api/get-open-interest';
import { useTicker24hr } from '@/features/markets/api/get-ticker-24hr';
import useSymbol from '@/hooks/use-symbol';
import { useListenKey } from '@/lib/api/listen-key';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { getMarketStore } from '@/store/use-markets-store';
import { useWebSocketStore } from '@/store/use-websocket-store';
import { WsType } from '@/types';

const PUBLIC_WS_URL = `${import.meta.env.VITE_WS_V2_URL}?version=1.0&xwebsocketserver=restserver0`;
const PRIVATE_WS_URL = `${import.meta.env.VITE_WS_URL}?version=1.0&xwebsocketserver=restserver`;
const PRIVATE_WS_V2_URL = `${import.meta.env.VITE_WS_V2_URL}/private`;

const PUBLIC_CHANNELS = ['ticker'];

/**
 * WebSocket Handler Component
 *
 * This component manages the WebSocket connection related to the business logic of the app.
 *
 * Key features:
 * - Initializes public and private WebSocket connections, exported as `publicWs` and `privateWs`
 * - Manages auth and listen key (with refresh) for private
 * - Sets up default message handlers that put updates into the store
 * - Disconnects private connection on switch wallet/disconnect
 *
 * @component
 */

export default function WebSocketHandler() {
  const { hasPublicWs, privateWsByAddress, isPublicConnected } =
    useWebSocketStore(
      useShallow((state) => ({
        hasPublicWs: !!state.publicWs,
        isPublicConnected: state.isConnected[WsType.PUBLIC],
        privateWsByAddress: state.privateWsByAddress,
      })),
    );

  const {
    _hasHydrated,
    address,
    fundedModeActive,
    fundedUpdateAddresses,
    accounts,
    authParamsByAddress,
    getAuthParams,
  } = useAccountStore(
    useShallow((state) => ({
      _hasHydrated: state._hasHydrated,
      address: state.address,
      fundedModeActive: state.fundedModeActive,
      fundedUpdateAddresses: state.fundedUpdateAddresses,
      accounts: state.accounts,
      authParamsByAddress: state.authParamsByAddress,
      getAuthParams: state.getAuthParams,
    })),
  );

  const listenKeyPendingRef = useRef(new Set<string>());

  const { mutateAsync: getListenKey } = useListenKey();

  // Initialize Public WebSocket
  useEffect(() => {
    const { initializePublicWs } = useWebSocketStore.getState();

    initializePublicWs(PUBLIC_WS_URL);
  }, []);

  const privateAddresses = useMemo(() => {
    const addresses = new Set<string>();

    if (address) {
      addresses.add(address);
    }

    if (fundedModeActive) {
      fundedUpdateAddresses.forEach((fundedAddress) =>
        addresses.add(fundedAddress),
      );
    }

    return Array.from(addresses);
  }, [address, fundedModeActive, fundedUpdateAddresses]);

  useEffect(() => {
    if (!fundedModeActive) {
      return;
    }

    privateAddresses.forEach((targetAddress) => {
      if (listenKeyPendingRef.current.has(targetAddress)) {
        return;
      }

      const authParams = getAuthParams(targetAddress, { skipFallback: true });

      if (
        !authParams?.apiKey ||
        authParams.accountGroup === 2 ||
        !!authParams.listenKey
      )
        return;

      listenKeyPendingRef.current.add(targetAddress);

      getListenKey(targetAddress)
        .catch((error) => {
          console.warn(
            'Failed to get listen key for funded address',
            targetAddress,
            error,
          );
        })
        .finally(() => {
          listenKeyPendingRef.current.delete(targetAddress);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundedModeActive, privateAddresses, authParamsByAddress]);

  // Initialize Private WebSocket(s)
  useEffect(() => {
    const { initializePrivateWs, disconnectWs } = useWebSocketStore.getState();

    if (!_hasHydrated) {
      disconnectWs(WsType.PRIVATE);
      return;
    }

    const targetAddresses = privateAddresses.filter((targetAddress) => {
      const authParams = getAuthParams(targetAddress, { skipFallback: true });
      if (!authParams) return false;
      if (authParams.accountGroup === 2) return !!authParams.apiKey;
      return !!authParams.listenKey;
    });

    const targetKeys = new Set(
      targetAddresses.map((targetAddress) => targetAddress.toLowerCase()),
    );

    Object.keys(privateWsByAddress).forEach((existingAddress) => {
      if (!targetKeys.has(existingAddress)) {
        disconnectWs(WsType.PRIVATE, false, { address: existingAddress });
      }
    });

    targetAddresses.forEach((targetAddress) => {
      const authParams = getAuthParams(targetAddress, { skipFallback: true });

      let url: string;

      if (authParams?.accountGroup === undefined) return;
      if (authParams.accountGroup === 2) {
        url = `${PRIVATE_WS_V2_URL}?token=${authParams.apiKey}`;
      } else {
        if (!authParams.listenKey) return;
        url = `${PRIVATE_WS_URL}${authParams.accountGroup}&listenKey=${authParams.listenKey}`;
      }

      initializePrivateWs(targetAddress, url);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_hasHydrated, privateAddresses, privateWsByAddress, authParamsByAddress]);

  const openSymbols = useCurrentAccount(
    useShallow((state) => Object.keys(state.positions)),
  );

  const fundedOpenSymbols = useMemo(() => {
    if (!fundedModeActive) {
      return [];
    }

    const symbols = new Set<string>();

    fundedUpdateAddresses.forEach((address) => {
      const positions = accounts[address]?.positions;

      if (!positions) {
        return;
      }

      Object.keys(positions).forEach((positionSymbol) =>
        symbols.add(positionSymbol),
      );
    });

    return Array.from(symbols);
  }, [fundedModeActive, fundedUpdateAddresses, accounts]);

  useEffect(() => {
    const { increaseDependenciesForSymbol, decreaseDependenciesForSymbol } =
      useWebSocketStore.getState();

    openSymbols.forEach((symbol) => increaseDependenciesForSymbol(symbol));

    return () => {
      openSymbols.forEach((symbol) => decreaseDependenciesForSymbol(symbol));
    };
  }, [openSymbols]);

  const symbol = useSymbol();

  const dependenciesBySymbol = useWebSocketStore(
    useShallow((state) => state.dependenciesBySymbol),
  );

  const symbols = useMemo(() => {
    const symbolsSet = new Set<string>();

    Object.keys(dependenciesBySymbol).forEach((key) => {
      if (dependenciesBySymbol[key] > 0) {
        symbolsSet.add(key);
      }
    });

    fundedOpenSymbols.forEach((fundedSymbol) => symbolsSet.add(fundedSymbol));

    if (symbol) {
      symbolsSet.add(symbol);
    }

    return Array.from(symbolsSet);
  }, [symbol, dependenciesBySymbol, fundedOpenSymbols]);

  const depthSymbols = useMemo(() => {
    const symbolsSet = new Set<string>(openSymbols);

    fundedOpenSymbols.forEach((fundedSymbol) => symbolsSet.add(fundedSymbol));

    if (symbol) {
      symbolsSet.add(symbol);
    }

    return Array.from(symbolsSet);
  }, [symbol, openSymbols, fundedOpenSymbols]);

  const symbolsKey = useMemo(() => symbols.slice().sort().join(','), [symbols]);

  const depthSymbolsKey = useMemo(
    () => depthSymbols.slice().sort().join(','),
    [depthSymbols],
  );

  useEffect(() => {
    if (!hasPublicWs || !isPublicConnected) return;
    const { publicWs } = useWebSocketStore.getState();
    if (!publicWs) return;
    const symbolsList = symbolsKey ? symbolsKey.split(',') : [];
    const channels = PUBLIC_CHANNELS.flatMap((channel) =>
      symbolsList.map((symbol) => `${symbol}@${channel}`),
    );
    if (channels.length === 0) return;
    publicWs.subscribeToChannels(channels);
    return () => {
      publicWs.unsubscribeFromChannels(channels);
    };
  }, [symbolsKey, hasPublicWs, isPublicConnected]);

  const activeTickSize = useStore(
    getMarketStore(symbol ?? ''),
    (state) => state.activeTickSize,
  );

  useEffect(() => {
    if (!hasPublicWs || !isPublicConnected) return;
    const { publicWs } = useWebSocketStore.getState();
    if (!publicWs) return;
    const symbolsList = depthSymbolsKey ? depthSymbolsKey.split(',') : [];
    const channels = symbolsList.map((depthSymbol) => {
      const { marketSpec, activeTickSize: actualActiveTickSize } =
        getMarketStore(depthSymbol).getState();
      const tickSize = actualActiveTickSize || marketSpec.defaultTickSize;
      return tickSize
        ? `${depthSymbol}@depth_${tickSize}`
        : `${depthSymbol}@depth`;
    });
    if (channels.length === 0) return;
    publicWs.subscribeToChannels(channels);
    return () => {
      publicWs.unsubscribeFromChannels(channels);
    };
  }, [depthSymbolsKey, hasPublicWs, isPublicConnected, activeTickSize]);

  useOpenInterest({
    symbols,
    disabled: symbols.length === 0,
  });

  useTicker24hr({
    symbols,
    disabled: symbols.length === 0,
  });

  return <></>;
}
